/**
 * Schritt 1 „Quelle" und der Einstieg in Schritt 2 „Auswahl" (§11 des Umsetzungsplans).
 *
 * Drei Regeln der Oberflaeche, die hier schon gelten:
 *   1. Was schon richtig ist, bleibt unsichtbar — die Profil-Einstellungen stehen
 *      aufklappbar unter „Was dieses Profil festlegt", nicht als Formular.
 *   2. Pro Bildschirm eine Aufgabe: hier wird nur die Quelle gewaehlt und geprueft.
 *   3. Jede Einstellung erklaert sich mit einem Satz Klartext, keine nackten Plenty-Ids.
 *
 * Es wird nichts geschrieben und nichts gespeichert — die Seite liest. „Artikel
 * bilden" macht dabei mehr als „Datei anlesen": es gruppiert die Zeilen zu Artikeln
 * und prueft sie gegen den Bestand (Registry, plus begrenzte Live-Gegenproben).
 */
import { useCallback, useEffect, useState } from 'react';
import { FileSpreadsheet, Images, RefreshCw } from 'lucide-react';
import { api } from '@libero/core/services/api';
import { Button } from '@libero/core/components/ui/button';
import ArtikelListe from '../components/ArtikelListe';
import BetriebsartWahl, { betriebsartName, type Betriebsart } from '../components/BetriebsartWahl';
import LaufButton from '../components/LaufButton';
import Vorsortierung, { filtere, type GruppenSchluessel } from '../components/Vorsortierung';
import KennzahlenLeiste from '../components/KennzahlenLeiste';
import MeldungsListe from '../components/MeldungsListe';
import OffeneArbeit from '../components/OffeneArbeit';
import VorschauTabelle from '../components/VorschauTabelle';
import { megabyte, zahl, zeitpunkt } from '../lib/format';
import type {
  ArtikelVorschauAntwort,
  DateiVorschauAntwort,
  DriveOrdner,
  QuellenAntwort,
  StatusAntwort,
} from '../lib/typen';

export default function QuellePage() {
  const [status, setStatus] = useState<StatusAntwort | null>(null);
  const [quellen, setQuellen] = useState<QuellenAntwort | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);

  /**
   * Der erste Schritt und der rote Faden: Was soll mit den Artikeln passieren? Die
   * Antwort wandert in den Entwurf und bestimmt dort, welcher Pfad sichtbar ist.
   * Vorbelegt ist nichts — die Frage soll bewusst beantwortet werden.
   */
  const [betriebsart, setBetriebsart] = useState<Betriebsart | null>(null);
  const [profil, setProfil] = useState<string>('');
  /**
   * Mehrere Dateien sind erlaubt und bei JAKO Pflicht: TS, CTW und Neuheiten
   * überschneiden sich, decken einander aber nicht — 8 von 93 CTW-Artikeln stehen in
   * keiner anderen Datei. Zusammengeführt wird über die EAN entdoppelt.
   */
  const [dateiIds, setDateiIds] = useState<string[]>([]);
  /**
   * Bildordner in der Rangfolge des Anwenders. Mehrfachauswahl, weil die Ordner sich
   * ergaenzen: gemessen am uhlsport-Katalog deckt der beste Einzelordner 77,3 % der
   * Artikel ab, alle drei zusammen 94,3 % (+158 Artikel). Der oberste gewaehlte
   * Ordner gewinnt, wenn zwei dieselbe Materialnummer fuehren.
   */
  const [bildOrdner, setBildOrdner] = useState<string[]>([]);

  const [vorschau, setVorschau] = useState<DateiVorschauAntwort | null>(null);
  const [liest, setLiest] = useState(false);

  const [artikelVorschau, setArtikelVorschau] = useState<ArtikelVorschauAntwort | null>(null);
  /** Welche Gruppe die Liste zeigt. Nur Anzeige — die Vorschau waehlt noch nichts aus. */
  const [gruppe, setGruppe] = useState<GruppenSchluessel>('alle');
  const [bildetArtikel, setBildetArtikel] = useState(false);
  const [speichert, setSpeichert] = useState(false);
  const [gespeichert, setGespeichert] = useState<string | null>(null);

  const laden = useCallback(async () => {
    setLaedt(true);
    setFehler(null);
    try {
      const [statusAntwort, quellenAntwort] = await Promise.all([
        api.get<{ data: StatusAntwort }>('/programmes/artikelimport/status'),
        api.get<{ data: QuellenAntwort }>('/programmes/artikelimport/quellen'),
      ]);
      const s = statusAntwort.data.data;
      const q = quellenAntwort.data.data;
      setStatus(s);
      setQuellen(q);
      if (!profil && s.profile.length > 0) setProfil(s.profile[0].schluessel);
      if (q.ftpVorauswahl) setBildOrdner([q.ftpVorauswahl]);
    } catch (err: any) {
      setFehler(err?.response?.data?.error?.message ?? err?.message ?? 'Laden fehlgeschlagen.');
    } finally {
      setLaedt(false);
    }
  }, [profil]);

  useEffect(() => { void laden(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** Datei an- oder abwählen. Die Reihenfolge der Auswahl bleibt erhalten. */
  const dateiUmschalten = useCallback((id: string) => {
    setDateiIds(alt => (alt.includes(id) ? alt.filter(d => d !== id) : [...alt, id]));
    setVorschau(null);
    setArtikelVorschau(null);
    setGespeichert(null);
  }, []);

  const dateiLesen = useCallback(async () => {
    // Angelesen wird immer nur die erste Datei — der Schritt soll zeigen, wie die
    // Spalten ankommen, nicht schon zusammenführen.
    const [ersteDatei] = dateiIds;
    if (!profil || !ersteDatei) return;
    setLiest(true);
    setFehler(null);
    setVorschau(null);
    setArtikelVorschau(null);
    setGespeichert(null);
    try {
      const antwort = await api.get<{ data: DateiVorschauAntwort }>(
        '/programmes/artikelimport/datei-vorschau',
        { params: { profil, datei: ersteDatei, zeilen: 25 } },
      );
      setVorschau(antwort.data.data);
    } catch (err: any) {
      setFehler(err?.response?.data?.error?.message ?? err?.message ?? 'Datei nicht lesbar.');
    } finally {
      setLiest(false);
    }
  }, [profil, dateiIds]);

  const artikelBilden = useCallback(async () => {
    if (!profil || dateiIds.length === 0) return;
    setBildetArtikel(true);
    setFehler(null);
    setArtikelVorschau(null);
    setGruppe('alle');
    try {
      const antwort = await api.get<{ data: ArtikelVorschauAntwort }>(
        '/programmes/artikelimport/artikel-vorschau',
        // Der Bildordner geht mit: ohne ihn bliebe die Bild-Ampel leer, weil gar
        // nicht nachgesehen wurde.
        {
          params: {
            profil, datei: dateiIds.join(','),
            bildOrdner: bildOrdner.length > 0 ? bildOrdner.join(',') : undefined,
          },
        },
      );
      setArtikelVorschau(antwort.data.data);
    } catch (err: any) {
      setFehler(err?.response?.data?.error?.message ?? err?.message ?? 'Abgleich fehlgeschlagen.');
    } finally {
      setBildetArtikel(false);
    }
  }, [profil, dateiIds, bildOrdner]);

  const alsEntwurfSpeichern = useCallback(async () => {
    if (!profil || dateiIds.length === 0 || !artikelVorschau) return;
    setSpeichert(true);
    setFehler(null);
    setGespeichert(null);
    try {
      const antwort = await api.post<{ data: { entwurfId: number; artikel: number } }>(
        '/programmes/artikelimport/entwuerfe',
        {
          profil,
          datei: dateiIds,
          bildOrdner,
          betriebsart: betriebsart ?? 'neuanlage',
          name: `${betriebsartName(betriebsart ?? 'neuanlage')}: ${profil} — ${artikelVorschau.dateiname}`,
        },
      );
      setGespeichert(
        `Entwurf ${antwort.data.data.entwurfId} mit ${antwort.data.data.artikel} Artikeln `
        + `gespeichert. Weiter im Reiter „Entwürfe".`,
      );
    } catch (err: any) {
      setFehler(err?.response?.data?.error?.message ?? 'Speichern fehlgeschlagen.');
    } finally {
      setSpeichert(false);
    }
  }, [profil, dateiIds, bildOrdner, artikelVorschau, betriebsart]);

  const aktuellesProfil = status?.profile.find(p => p.schluessel === profil) ?? null;
  const preisset = status?.preissets.find(s => s.profilSchluessel === profil)
    ?? status?.preissets.find(s => s.istStandard)
    ?? null;

  // Der Drive-Ordner, der zum gewaehlten Hersteller passt — der Rest bleibt waehlbar,
  // weil der Ordnername nur ein Hinweis ist, keine Wahrheit (Leitplanke 4).
  const ordnerSortiert: DriveOrdner[] = [...(quellen?.drive ?? [])].sort((a, b) => {
    const passtA = a.herstellerHinweis === profil ? 0 : 1;
    const passtB = b.herstellerHinweis === profil ? 0 : 1;
    return passtA - passtB || a.name.localeCompare(b.name, 'de');
  });

  return (
    <div className="space-y-6 p-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">Artikelimport</h1>
          <p className="mt-1 max-w-3xl text-sm text-[#1A1A1A]/70">
            Neue Herstellerartikel aus einer Katalogdatei anlegen. Dieser Schritt waehlt
            die Quelle und zeigt, was der Import darin liest — geschrieben wird hier noch
            nichts.
          </p>
        </div>
        <Button variant="outline" onClick={() => void laden()} disabled={laedt}>
          <RefreshCw className={`mr-2 h-4 w-4 ${laedt ? 'animate-spin' : ''}`} />
          {/* Der Aufruf holt Drive-Ordner UND das FTP-Listing (54 Ordner) — das dauert
              spuerbar, also steht auch hier dran, was passiert. */}
          {laedt ? 'Drive und FTP werden gelesen …' : 'Neu laden'}
        </Button>
      </header>

      {fehler && (
        <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-900">
          {fehler}
        </div>
      )}

      {quellen && <MeldungsListe meldungen={quellen.meldungen} />}

      {/* ── Was soll passieren? ───────────────────────────────────────────── */}
      <section className="space-y-3 rounded border-2 border-[#1A1A1A] p-4">
        <h2 className="font-semibold text-[#1A1A1A]">1. Was soll passieren?</h2>
        <BetriebsartWahl wert={betriebsart} aufWahl={b => { setBetriebsart(b); setGespeichert(null); }} />
        {betriebsart === 'felder' && (
          <p className="text-sm text-[#1A1A1A]/70">
            Beim Aktualisieren sind die Artikel das Ziel, die <strong>schon in Plenty
            stehen</strong>. Die Vorschau unten zeigt sie als „existiert bereits" —
            hier ist das kein Hindernis, sondern die Voraussetzung.
          </p>
        )}
      </section>

      {/* ── Hersteller ────────────────────────────────────────────────────── */}
      <section className={`space-y-3 rounded border border-[#DAD9D9] p-4 ${betriebsart ? '' : 'opacity-50'}`}>
        <h2 className="font-semibold text-[#1A1A1A]">2. Hersteller</h2>
        <div className="flex flex-wrap gap-2">
          {(status?.profile ?? []).map(p => (
            <button
              key={p.schluessel}
              type="button"
              onClick={() => { setProfil(p.schluessel); setVorschau(null); setDateiIds([]); }}
              className={`rounded border px-3 py-2 text-sm ${
                profil === p.schluessel
                  ? 'border-[#1A1A1A] bg-[#E9FF57] text-[#1A1A1A]'
                  : 'border-[#DAD9D9] bg-white text-[#1A1A1A] hover:border-[#1A1A1A]'
              }`}
            >
              {p.hersteller}
            </button>
          ))}
          {(status?.profile ?? []).length === 0 && !laedt && (
            <p className="text-sm text-[#1A1A1A]/60">
              Noch kein Hersteller-Profil eingerichtet.
            </p>
          )}
        </div>

        {aktuellesProfil && (
          <details className="text-sm">
            <summary className="cursor-pointer text-[#1A1A1A]/70">
              Was dieses Profil festlegt
            </summary>
            <ul className="mt-2 space-y-1 text-[#1A1A1A]/80">
              <li>
                Tabellenblatt:{' '}
                <strong>{aktuellesProfil.quelle?.blatt ?? 'erstes Blatt'}</strong>
                {' '}— die Kopfzeile wird beim Lesen gesucht, nicht vorausgesetzt.
              </li>
              <li>Bildordner: <strong>{aktuellesProfil.bildOrdner ?? 'keiner'}</strong></li>
              {preisset && (
                <li>
                  Preise: <strong>{preisset.name}</strong> —{' '}
                  {preisset.zeilen.map(z => `${z.preisart} = ${z.ausdruck}`).join(' · ')}
                </li>
              )}
              {aktuellesProfil.notiz && <li className="text-[#1A1A1A]/60">{aktuellesProfil.notiz}</li>}
            </ul>
          </details>
        )}
      </section>

      {/* ── Katalogdatei ──────────────────────────────────────────────────── */}
      <section className="space-y-3 rounded border border-[#DAD9D9] p-4">
        <h2 className="flex items-center gap-2 font-semibold text-[#1A1A1A]">
          <FileSpreadsheet className="h-4 w-4" /> 2. Katalogdatei(en) aus dem Drive
        </h2>
        <p className="text-sm text-[#1A1A1A]/70">
          Mehrere Dateien lassen sich zusammenführen — sinnvoll, wenn ein Hersteller
          denselben Katalog auf mehrere Listen verteilt (JAKO: TS, CTW, Neuheiten).
          Doppelte EANs werden dabei nur einmal gezählt.
        </p>

        {ordnerSortiert.map(ordner => (
          <div key={ordner.id} className="space-y-1">
            <p className="text-xs uppercase tracking-wide text-[#1A1A1A]/50">
              {ordner.name}
              {ordner.herstellerHinweis === profil && ' · passt zum gewaehlten Hersteller'}
            </p>
            <div className="space-y-1">
              {ordner.dateien.filter(d => d.lesbar).slice(0, 8).map(datei => (
                <label
                  key={datei.id}
                  className={`flex cursor-pointer items-center gap-3 rounded border px-3 py-2 text-sm ${
                    dateiIds.includes(datei.id)
                      ? 'border-[#1A1A1A] bg-[#E9FF57]/40'
                      : 'border-[#DAD9D9]'
                  }`}
                >
                  <input
                    type="checkbox"
                    name="katalogdatei"
                    checked={dateiIds.includes(datei.id)}
                    onChange={() => dateiUmschalten(datei.id)}
                  />
                  <span className="flex-1">{datei.name}</span>
                  <span className="tabular-nums text-[#1A1A1A]/60">
                    {megabyte(datei.groesseBytes)}
                  </span>
                  <span className="tabular-nums text-[#1A1A1A]/60">
                    {zeitpunkt(datei.geaendertAm)}
                  </span>
                </label>
              ))}
              {ordner.dateien.filter(d => d.lesbar).length === 0 && (
                <p className="text-sm text-[#1A1A1A]/50">keine .xlsx-Datei in diesem Ordner</p>
              )}
            </div>
          </div>
        ))}

        <div className="flex flex-wrap items-start gap-4">
          <LaufButton
            variant="outline"
            laeuft={liest}
            laufText="Datei wird gelesen"
            dauerHinweis="Herunterladen und erste Zeilen, meist unter 1 Minute"
            onClick={() => void dateiLesen()}
            disabled={!betriebsart || !profil || dateiIds.length === 0 || bildetArtikel}
          >
            Datei anlesen
          </LaufButton>
          <LaufButton
            laeuft={bildetArtikel}
            laufText="Artikel werden gebildet"
            schritt={schrittText(dateiIds.length)}
            dauerHinweis="dauert bei uhlsport 1–2 Minuten"
            onClick={() => void artikelBilden()}
            disabled={!betriebsart || !profil || dateiIds.length === 0 || liest}
            className="bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d]"
          >
            {dateiIds.length > 1
              ? `${dateiIds.length} Dateien zusammenführen und abgleichen`
              : 'Artikel bilden und abgleichen'}
          </LaufButton>
        </div>
        <p className="text-xs text-[#1A1A1A]/50">
          „Datei anlesen" zeigt die ersten Zeilen der ZUERST gewählten Datei, wie sie
          dort stehen. „Artikel bilden" liest alle gewählten Dateien, fasst die Zeilen zu
          Artikeln zusammen und prüft jeden gegen den Bestand — das dauert länger und
          macht Leseabfragen gegen Plenty.
        </p>
      </section>

      {/* ── Bildordner ────────────────────────────────────────────────────── */}
      <section className="space-y-3 rounded border border-[#DAD9D9] p-4">
        <h2 className="flex items-center gap-2 font-semibold text-[#1A1A1A]">
          <Images className="h-4 w-4" /> 4. Bildordner auf dem FTP
        </h2>
        <p className="text-sm text-[#1A1A1A]/70">
          Mehrere Ordner sind erlaubt. Vorgewählt ist der neueste, in dem tatsächlich
          Bilder liegen. Artikel ohne Bild werden trotzdem angelegt und markiert.
        </p>
        <p className="text-sm text-amber-800">
          Die Ordner lösen einander nicht ab, sie <strong>ergänzen sich</strong>: gemessen
          am uhlsport-Katalog (931 Materialnummern, 22.09.2026) deckt der beste einzelne
          Ordner <strong>720 Artikel (77,3 %)</strong> ab, alle drei zusammen{' '}
          <strong>878 (94,3 %)</strong>. <code>/uhlsport2025</code> und{' '}
          <code>/uhlsport2025_2</code> sind dabei deckungsgleich —{' '}
          <code>/uhlsport09_2026</code> hat mit beiden keine einzige Nummer gemeinsam.
        </p>
        <p className="text-sm text-[#1A1A1A]/70">
          Führen zwei Ordner dieselbe Materialnummer, gewinnt der <strong>weiter oben
          stehende</strong> — und zwar mit allen seinen Bildern dieser Nummer. Die
          Reihenfolge ergibt sich daraus, in welcher du anklickst.
        </p>
        <div className="space-y-1">
          {(quellen?.ftp ?? []).map(ordner => {
            const rang = bildOrdner.indexOf(ordner.pfad);
            const gewaehlt = rang >= 0;
            return (
              <label
                key={ordner.pfad}
                className={`flex cursor-pointer items-center gap-3 rounded border px-3 py-2 text-sm ${
                  gewaehlt ? 'border-[#1A1A1A] bg-[#E9FF57]/40' : 'border-[#DAD9D9]'
                } ${ordner.bildAnzahl === 0 ? 'opacity-60' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={gewaehlt}
                  onChange={() => setBildOrdner(vorher => (
                    // Abwählen entfernt nur diesen Ordner, die Rangfolge der übrigen
                    // bleibt. Anwählen hängt hinten an — wer zuerst klickt, hat Vorrang.
                    gewaehlt ? vorher.filter(p => p !== ordner.pfad) : [...vorher, ordner.pfad]
                  ))}
                />
                {gewaehlt && (
                  <span
                    className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full
                      bg-[#1A1A1A] text-xs font-semibold tabular-nums text-white"
                    title={`Rang ${rang + 1} — gewinnt bei Doppelungen gegen alles darunter`}
                  >
                    {rang + 1}
                  </span>
                )}
                <span className="flex-1">{ordner.name}</span>
                <span className="tabular-nums text-[#1A1A1A]/70">
                  {zahl(ordner.bildAnzahl)} Bilder
                </span>
                {ordner.andereAnzahl > 0 && (
                  <span className="text-xs text-[#1A1A1A]/50">
                    + {zahl(ordner.andereAnzahl)} andere Dateien
                  </span>
                )}
              </label>
            );
          })}
          {(quellen?.ftp ?? []).length === 0 && !laedt && (
            <p className="text-sm text-[#1A1A1A]/60">Kein Bildordner erreichbar.</p>
          )}
        </div>

        {bildOrdner.length > 1 && (
          <p className="text-sm text-[#1A1A1A]">
            <strong>{bildOrdner.length} Ordner</strong> in dieser Rangfolge:{' '}
            {bildOrdner.map((p, i) => (
              <span key={p}>
                {i > 0 && ' → '}
                <code>{p}</code>
              </span>
            ))}
            . Wie viele Artikel damit ein Bild bekommen, steht nach „Artikel bilden" in
            der Bild-Ampel.
          </p>
        )}
        {bildOrdner.length === 0 && (
          <p className="text-sm text-[#1A1A1A]/60">
            Kein Ordner gewählt — die Artikel entstehen ohne Bild, und die Bild-Ampel
            bleibt grau (nicht rot: es wurde ja nicht gesucht).
          </p>
        )}
      </section>

      {/* ── Was in der Datei steht ────────────────────────────────────────── */}
      {vorschau && (
        <section className="space-y-3 rounded border border-[#DAD9D9] p-4">
          <h2 className="font-semibold text-[#1A1A1A]">
            Das steht in „{vorschau.dateiname}"
          </h2>
          <p className="text-sm text-[#1A1A1A]/70">
            Blatt <strong>{vorschau.blatt}</strong>, Kopfzeile in Zeile{' '}
            <strong>{vorschau.kopfzeile ?? '?'}</strong>, {zahl(vorschau.spalten.length)} Spalten.{' '}
            {zahl(vorschau.gelesen)} Zeilen gelesen, davon {zahl(vorschau.behalten)} behalten.
          </p>

          {Object.keys(vorschau.ausgefiltert).length > 0 && (
            <ul className="text-sm text-[#1A1A1A]/70">
              {Object.entries(vorschau.ausgefiltert).map(([grund, anzahl]) => (
                <li key={grund}>{zahl(anzahl)} × ausgefiltert: {grund}</li>
              ))}
            </ul>
          )}

          <MeldungsListe meldungen={vorschau.meldungen} />
          <VorschauTabelle zeilen={vorschau.zeilen} />

          <p className="text-xs text-[#1A1A1A]/50">
            Das sind die ersten {zahl(vorschau.zeilen.length)} Zeilen, ungruppiert.
            Für Artikel und Bestandsabgleich oben auf „Artikel bilden und abgleichen".
          </p>
        </section>
      )}

      {/* ── Artikel ───────────────────────────────────────────────────────── */}
      {artikelVorschau && (
        <section className="space-y-4 rounded border border-[#DAD9D9] p-4">
          <h2 className="font-semibold text-[#1A1A1A]">
            Das würde aus „{artikelVorschau.dateiname}" entstehen
          </h2>

          <KennzahlenLeiste daten={artikelVorschau} />

          {Object.keys(artikelVorschau.ausgefiltert).length > 0 && (
            <ul className="text-sm text-[#1A1A1A]/70">
              {Object.entries(artikelVorschau.ausgefiltert).map(([grund, anzahl]) => (
                <li key={grund}>{zahl(anzahl)} × ausgefiltert: {grund}</li>
              ))}
            </ul>
          )}

          <MeldungsListe meldungen={artikelVorschau.meldungen} />

          {/*
            Was die Ordnerwahl konkret bringt. Die Zahl steht bewusst hier und nicht
            nur als Ampel je Artikel: ob sich ein zweiter Ordner lohnt, sieht man erst
            an der Summe.
          */}
          {(artikelVorschau.bilder.ordnerListe?.length ?? 0) > 0 && (
            <p className="rounded bg-[#DAD9D9]/30 px-3 py-2 text-sm text-[#1A1A1A]">
              <strong>Bilder:</strong>{' '}
              {zahl(artikelVorschau.bilder.artikelMitBild
                + artikelVorschau.bilder.artikelTeilweise)}{' '}
              von {zahl(artikelVorschau.kennzahlen.artikel)} Artikeln bekommen mindestens
              ein Bild
              {artikelVorschau.bilder.artikelTeilweise > 0 && (
                <> (davon {zahl(artikelVorschau.bilder.artikelTeilweise)} nur teilweise)</>
              )}
              , {zahl(artikelVorschau.bilder.artikelOhneBild)} bleiben ohne — aus{' '}
              {artikelVorschau.bilder.ordnerListe?.length === 1
                ? <code>{artikelVorschau.bilder.ordnerListe[0]}</code>
                : <>{artikelVorschau.bilder.ordnerListe?.length} Ordnern</>}
              . Das kostet {zahl(artikelVorschau.bilder.zugeordnet)} Uploads und{' '}
              {zahl(artikelVorschau.bilder.verknuepfungen)} Verknüpfungen.
            </p>
          )}

          <OffeneArbeit daten={artikelVorschau} />

          {/* Vorsortieren: hier nur filtern — ausgewaehlt wird erst im Entwurf. */}
          <Vorsortierung
            artikel={artikelVorschau.artikel.map(a => ({
              schluessel: a.schluessel,
              existenz: a.existenz.status,
              ampelKategorie: a.kategorien.ampel,
              ampelBild: a.bilder.ampel,
            }))}
            aktiv={gruppe}
            aufGruppe={setGruppe}
          />

          <ArtikelListe
            artikel={filtere(
              artikelVorschau.artikel.map(a => ({
                ...a,
                existenz: a.existenz.status,
                ampelKategorie: a.kategorien.ampel,
                ampelBild: a.bilder.ampel,
              })),
              gruppe,
            )}
          />

          <div className="flex flex-wrap items-center gap-3 border-t border-[#DAD9D9] pt-4">
            <LaufButton
              laeuft={speichert}
              laufText="Entwurf wird gespeichert"
              dauerHinweis="rechnet Preise und Kategorien noch einmal durch"
              onClick={() => void alsEntwurfSpeichern()}
              className="bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d]"
            >
              Als Entwurf speichern
            </LaufButton>
            <span className="text-xs text-[#1A1A1A]/60">
              Legt Zeilen in der Libero-Datenbank an — in Plenty passiert weiterhin nichts.
            </span>
          </div>

          {gespeichert && (
            <p className="rounded border border-green-300 bg-green-50 p-3 text-sm text-green-900">
              {gespeichert}
            </p>
          )}
        </section>
      )}
    </div>
  );
}

/**
 * Was „Artikel bilden" gerade tut. Ein echter Zaehler kaeme nur ueber einen Job mit
 * Zwischenstand — bis dahin sagt die Zeile wenigstens, welche Schritte dranhaengen,
 * statt einen Prozentwert zu erfinden.
 */
function schrittText(dateien: number): string {
  return dateien > 1
    ? `${dateien} Dateien lesen · zusammenführen · gegen den Bestand prüfen`
    : 'Datei lesen · gruppieren · gegen den Bestand prüfen';
}
